/**
 * Utility functions for converting between Trade and TradeFormData
 */

import { Trade, TradeFormData } from "../types/trade";
import { calculateTradeValues } from "./tradeCalculations";

/**
 * Convert a Trade object to TradeFormData for editing
 */
export const tradeToFormData = (trade: Trade): TradeFormData => {
  // Format dates for input fields (YYYY-MM-DDTHH:mm format for datetime-local)
  const formatDateForInput = (date: Date | string): string => {
    const d = typeof date === "string" ? new Date(date) : date;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return {
    symbol: trade.symbol,
    type: trade.type,
    status: trade.status,
    entryDate: formatDateForInput(trade.entryDate),
    entryPrice: trade.entryPrice,
    entryQuantity: trade.entryQuantity,
    entryFees: trade.entryFees || 0,
    exitDate: trade.exitDate ? formatDateForInput(trade.exitDate) : undefined,
    exitPrice: trade.exitPrice,
    exitQuantity: trade.exitQuantity,
    exitFees: trade.exitFees || 0,
    strategy: trade.strategy,
    notes: trade.notes,
    tags: trade.tags || [],
    setupTags: trade.setupTags || [],
    mistakeTags: trade.mistakeTags || [],
    
    // Enhanced Journaling Fields
    preTradeRationale: trade.preTradeRationale,
    preTradeTargets: trade.preTradeTargets,
    preTradeRiskPlan: trade.preTradeRiskPlan,
    preTradeMarketConditions: trade.preTradeMarketConditions,
    postTradeReview: trade.postTradeReview,
    postTradeMistakes: trade.postTradeMistakes,
    postTradeLessons: trade.postTradeLessons,
    postTradeSuccesses: trade.postTradeSuccesses,
    marketDirection: trade.marketDirection,
    sectorMovements: trade.sectorMovements,
    volumeAnalysis: trade.volumeAnalysis,
    marketNews: trade.marketNews,
    sentimentIndicators: trade.sentimentIndicators,
    
    // Psychological Tracking
    preTradeConfidence: trade.preTradeConfidence,
    preTradeEmotion: trade.preTradeEmotion,
    duringTradeEmotion: trade.duringTradeEmotion,
    followedPlan: trade.followedPlan,
    temptedEarlyExit: trade.temptedEarlyExit,
    addedToPosition: trade.addedToPosition,
    postTradeEmotion: trade.postTradeEmotion,
    emotionsInvolved: trade.emotionsInvolved,
    
    // Trade Plan vs. Execution
    plannedEntryPrice: trade.plannedEntryPrice,
    plannedExitPrice: trade.plannedExitPrice,
    plannedStopLoss: trade.plannedStopLoss,
    plannedTakeProfit: trade.plannedTakeProfit,
    plannedRisk: trade.plannedRisk,
    plannedDuration: trade.plannedDuration,
    mfe: trade.mfe,
    mae: trade.mae,
  };
};

/**
 * Convert TradeFormData to Trade object
 * Creates a new Trade with calculated values
 */
export const formDataToTrade = (
  formData: TradeFormData,
  userId: string,
  tradeId?: string
): Trade => {
  const now = new Date().toISOString();
  const entryDate = new Date(formData.entryDate);
  const exitDate = formData.exitDate ? new Date(formData.exitDate) : undefined;

  // Calculate trade values
  const calculated = calculateTradeValues({
    entryPrice: formData.entryPrice,
    entryQuantity: formData.entryQuantity,
    entryFees: formData.entryFees,
    exitPrice: formData.status === "closed" ? formData.exitPrice : undefined,
    exitQuantity: formData.status === "closed" ? formData.exitQuantity : undefined,
    exitFees: formData.status === "closed" ? (formData.exitFees || 0) : undefined,
    status: formData.status,
    type: formData.type,
  });

  return {
    id: tradeId || `trade-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    symbol: formData.symbol,
    type: formData.type,
    status: formData.status,
    entryDate: entryDate.toISOString(),
    entryPrice: formData.entryPrice,
    entryQuantity: formData.entryQuantity,
    entryFees: formData.entryFees,
    exitDate: exitDate?.toISOString(),
    exitPrice: formData.exitPrice,
    exitQuantity: formData.exitQuantity,
    exitFees: formData.exitFees,
    entryValue: calculated.entryValue,
    exitValue: calculated.exitValue,
    grossPnL: calculated.grossPnL,
    netPnL: calculated.netPnL,
    returnPercent: calculated.returnPercent,
    roi: calculated.roi,
    strategy: formData.strategy,
    notes: formData.notes,
    tags: formData.tags,
    setupTags: formData.setupTags,
    mistakeTags: formData.mistakeTags,
    
    // Enhanced Journaling Fields
    preTradeRationale: formData.preTradeRationale,
    preTradeTargets: formData.preTradeTargets,
    preTradeRiskPlan: formData.preTradeRiskPlan,
    preTradeMarketConditions: formData.preTradeMarketConditions,
    postTradeReview: formData.postTradeReview,
    postTradeMistakes: formData.postTradeMistakes,
    postTradeLessons: formData.postTradeLessons,
    postTradeSuccesses: formData.postTradeSuccesses,
    marketDirection: formData.marketDirection,
    sectorMovements: formData.sectorMovements,
    volumeAnalysis: formData.volumeAnalysis,
    marketNews: formData.marketNews,
    sentimentIndicators: formData.sentimentIndicators,
    
    // Psychological Tracking
    preTradeConfidence: formData.preTradeConfidence,
    preTradeEmotion: formData.preTradeEmotion,
    duringTradeEmotion: formData.duringTradeEmotion,
    followedPlan: formData.followedPlan,
    temptedEarlyExit: formData.temptedEarlyExit,
    addedToPosition: formData.addedToPosition,
    postTradeEmotion: formData.postTradeEmotion,
    emotionsInvolved: formData.emotionsInvolved,
    
    // Trade Plan vs. Execution
    plannedEntryPrice: formData.plannedEntryPrice,
    plannedExitPrice: formData.plannedExitPrice,
    plannedStopLoss: formData.plannedStopLoss,
    plannedTakeProfit: formData.plannedTakeProfit,
    plannedRisk: formData.plannedRisk,
    plannedDuration: formData.plannedDuration,
    mfe: formData.mfe,
    mae: formData.mae,
    
    createdAt: now,
    updatedAt: now,
    userId: userId,
  };
};

